import isEqual from 'lodash/isEqual';
import { useState, useCallback, useEffect } from 'react';
// @mui
import {
  Card,
  Table,
  Button,
  Tooltip,
  Container,
  TableBody,
  IconButton,
  TableContainer,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';

// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hook';
// hooks
import { useSettingsContext } from 'src/components/settings';
import { useBoolean } from 'src/hooks/use-boolean';
// components
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import {
  useTable,
  emptyRows,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
  TableSelectedAction,
  TableNoData,
} from 'src/components/table';
import { _roles } from 'src/_mock';
import Label from 'src/components/label';
import { getColorForStatus } from 'src/custom_components/Table/helper/getColorStatus';
// context
import { useShelfActions, useShelfData } from 'src/context/providers/ShelfContext';
// custom components
import UserTableRow from '../../custom_components/Table/customer-table-row';
import UserTableToolbar from '../../custom_components/Table/customer-table-toolbar';
import UserTableFiltersResult from '../../custom_components/Table/customer-table-filters-result';
import CustomerDrawerComponent from './customer-drawer-component';
import CreateDrawer from './CreateDrawer';
// _mock

export default function TcTable({ STATUS_OPTIONS, TABLE_HEAD, defaultFilters, tableNames }) {
  const table = useTable();
  const settings = useSettingsContext();
  const router = useRouter();
  const confirm = useBoolean();

  const { filteredShelves } = useShelfData();
  const { getFilteredShelves, deleteShelf } = useShelfActions();

  const [filters, setFilters] = useState({
    ...defaultFilters,
    page: 1,
    take: 25,
  });
  const [aRow, setARow] = useState({});
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);

  useEffect(() => {
    getFilteredShelves(filters);
  }, [filters, getFilteredShelves]);

  const handleFilters = useCallback(
    (name, value) => {
      table.onResetPage();
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleEditRow = useCallback(
    (id) => {
      router.push(paths.dashboard.shelf.edit(id));
    },
    [router]
  );

  const handleResetFilters = useCallback(() => {
    setFilters({
      ...defaultFilters,
      page: 1,
      take: 25,
    });
  }, [defaultFilters]);

  const handleDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleOrderDrawerToggle = () => {
    setIsOrderDrawerOpen(!isOrderDrawerOpen);
  };

  const handleDeleteRow = useCallback(
    (e, id) => {
      e.stopPropagation();
      deleteShelf([id]);
      table.onUpdatePageDeleteRow(filteredShelves?.shelves?.length);
    },
    [deleteShelf, filteredShelves, table]
  );

  const handleDeleteRows = useCallback(() => {
    deleteShelf(table.selected);
    confirm.onFalse();
  }, [deleteShelf, table.selected, confirm]);

  const handlePageChange = (event, newPage) => {
    setFilters((prevState) => ({
      ...prevState,
      page: newPage + 1,
    }));
  };

  const handleRowsPerPageChange = (event) => {
    setFilters((prevState) => ({
      ...prevState,
      take: parseInt(event.target.value, 10),
      page: 1,
    }));
  };

  const denseHeight = table.dense ? 52 : 72;
  const canReset = !isEqual(defaultFilters, filters);
  const notFound =
    (!filteredShelves?.shelves?.length && canReset) || !filteredShelves?.shelves?.length;

  return (
    <div>
      {!filteredShelves?.shelves?.length ? <LinearProgress color="success" /> : ''}

      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <CreateDrawer
          isDrawerOpen={isOrderDrawerOpen}
          handleDrawerToggle={handleOrderDrawerToggle}
        />

        <CustomBreadcrumbs
          heading="Shelf List"
          action={
            <Button
              onClick={handleOrderDrawerToggle}
              variant="contained"
              startIcon={<Iconify icon="mingcute:add-line" />}
            >
              New shelf
            </Button>
          }
          sx={{
            mb: { xs: 3, md: 5 },
          }}
        />

        {Object.keys(aRow).length > 0 && (
          <CustomerDrawerComponent
            isDrawerOpen={isDrawerOpen}
            handleDrawerToggle={handleDrawerToggle}
            aRow={aRow}
            setARow={setARow}
          />
        )}

        <Card>
          <Tabs
            value={filters.status}
            onChange={(e, value) => handleFilters('status', value)}
            style={{ marginLeft: '15px' }}
            sx={{
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {STATUS_OPTIONS?.map((tab) => (
              <Tab
                key={tab.value}
                iconPosition="end"
                value={tab.value}
                label={tab.label}
                icon={
                  <Label
                    variant={
                      ((tab.value === 'all' || tab.value === filters.status) && 'filled') || 'soft'
                    }
                    color={getColorForStatus(tab.value)}
                  >
                    {tab.value === 'all' && filteredShelves?.shelves?.length}
                  </Label>
                }
              />
            ))}
          </Tabs>

          <UserTableToolbar filters={filters} onFilters={handleFilters} roleOptions={_roles} />

          {canReset && (
            <UserTableFiltersResult
              filters={filters}
              onFilters={handleFilters}
              onResetFilters={handleResetFilters}
              results={filteredShelves?.shelves?.length}
              sx={{ p: 2.5, pt: 0 }}
            />
          )}

          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <TableSelectedAction
              dense={table.dense}
              numSelected={table.selected.length}
              rowCount={filteredShelves?.shelves?.length}
              onSelectAllRows={(checked) =>
                table.onSelectAllRows(
                  checked,
                  filteredShelves?.shelves?.map((row) => row.id)
                )
              }
              action={
                <Tooltip title="Delete">
                  <IconButton color="primary" onClick={confirm.onTrue}>
                    <Iconify icon="solar:trash-bin-trash-bold" />
                  </IconButton>
                </Tooltip>
              }
            />

            <Scrollbar>
              <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
                <TableHeadCustom
                  order={table.order}
                  orderBy={table.orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={filteredShelves?.shelves?.length}
                  numSelected={table.selected.length}
                  onSort={table.onSort}
                  onSelectAllRows={(checked) =>
                    table.onSelectAllRows(
                      checked,
                      filteredShelves?.shelves?.map((row) => row.id)
                    )
                  }
                />

                <TableBody>
                  {filteredShelves?.shelves
                    ?.slice(
                      table.page * table.rowsPerPage,
                      table.page * table.rowsPerPage + table.rowsPerPage
                    )
                    ?.map((row) => (
                      <UserTableRow
                        key={row.id}
                        row={row}
                        selected={table.selected.includes(row.id)}
                        onSelectRow={() => table.onSelectRow(row.id)}
                        onDeleteRow={(e) => handleDeleteRow(e, row.id)}
                        onEditRow={() => handleEditRow(row.id)}
                        handleDrawerToggle={handleDrawerToggle}
                        setARow={setARow}
                        tableNames={tableNames}
                      />
                    ))}

                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(
                      table.page,
                      table.rowsPerPage,
                      filteredShelves?.shelves?.length
                    )}
                  />

                  {notFound && <TableNoData notFound={notFound} />}
                </TableBody>
              </Table>
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={filteredShelves?.total || 0}
            page={filters.page - 1}
            rowsPerPage={filters.take}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            dense={table.dense}
            onChangeDense={table.onChangeDense}
          />
        </Card>
      </Container>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete"
        content={
          <>
            Are you sure you want to delete <strong>{table.selected.length}</strong> items?
          </>
        }
        action={
          <Button variant="contained" color="error" onClick={handleDeleteRows}>
            Delete
          </Button>
        }
      />
    </div>
  );
}

TcTable.propTypes = {
  STATUS_OPTIONS: PropTypes.array,
  TABLE_HEAD: PropTypes.array,
  defaultFilters: PropTypes.object,
  tableNames: PropTypes.array,
};
